import { BNN_CHAPTERS } from 'schools/bnn/data/bnnSyllabus';
import { JAIMINI_CHAPTERS } from 'schools/jaimini/data/jaiminiSyllabus';
import { KP_CHAPTERS } from 'schools/kp/data/kpSyllabus';
import { LAL_KITAB_CHAPTERS } from 'schools/lalKitab/data/lalKitabSyllabus';
import { NUMEROLOGY_CHAPTERS } from 'schools/numerology/data/numerologySyllabus';
import { PANCHANG_CHAPTERS } from 'schools/panchang/data/panchangSyllabus';
import { SWARA_CHAPTERS } from 'schools/swaraVigyan/data/swaraSyllabus';
import { YANTRA_CHAPTERS } from 'schools/yantraVidya/data/yantraVidyaSyllabus';
import { TANTRA_CHAPTERS } from 'schools/tantra/data/tantraSyllabus';
import { VASTU_CHAPTERS } from 'schools/vastu/data/vastuSyllabus';
import { SURYA_SIDDHANTA_CHAPTERS } from 'schools/suryaSiddhanta/data/suryaSiddhantaSyllabus';
import type { SchoolLearnChapter } from 'shared/schoolLearn/types';
import { SCHOOL_SEARCH_META } from 'shared/search/schoolSearchConfig';
import {
  findMatchSnippet,
  foldSearchText,
  scoreSearchMatch,
} from 'shared/search/searchUtils';
import type { GlobalSearchResult, SearchableSchoolSlug } from 'shared/search/types';

type SyllabusIndexEntry = {
  schoolSlug: SearchableSchoolSlug;
  topicId: string;
  moduleId: string;
  titleEn: string;
  titleHi: string;
  groupLabelEn: string;
  groupLabelHi: string;
  terms: string;
  icon: string;
  order: number;
};

function buildChapterIndex(
  schoolSlug: SearchableSchoolSlug,
  chapters: SchoolLearnChapter[],
): SyllabusIndexEntry[] {
  const schoolKeywords = SCHOOL_SEARCH_META[schoolSlug].keywords;
  let order = 0;
  const entries: SyllabusIndexEntry[] = [];

  for (const chapter of chapters) {
    for (const topic of chapter.topics) {
      entries.push({
        schoolSlug,
        topicId: topic.id,
        moduleId: chapter.id,
        titleEn: topic.title.en,
        titleHi: topic.title.hi,
        groupLabelEn: `Module ${chapter.number}: ${chapter.title.en}`,
        groupLabelHi: `मॉड्यूल ${chapter.number}: ${chapter.title.hi}`,
        terms: [
          topic.title.en,
          topic.title.hi,
          topic.summary.en,
          topic.summary.hi,
          chapter.title.en,
          chapter.title.hi,
          chapter.subtitle.en,
          chapter.subtitle.hi,
          schoolKeywords,
        ].join(' '),
        icon: topic.icon,
        order,
      });
      order += 1;
    }
  }

  return entries;
}

const SYLLABUS_SEARCH_INDEX: SyllabusIndexEntry[] = [
  ...buildChapterIndex('jaimini', JAIMINI_CHAPTERS),
  ...buildChapterIndex('kp', KP_CHAPTERS),
  ...buildChapterIndex('bnn', BNN_CHAPTERS),
  ...buildChapterIndex('lal-kitab', LAL_KITAB_CHAPTERS),
  ...buildChapterIndex('numerology', NUMEROLOGY_CHAPTERS),
  ...buildChapterIndex('surya-siddhanta', SURYA_SIDDHANTA_CHAPTERS),
  ...buildChapterIndex('panchang', PANCHANG_CHAPTERS),
  ...buildChapterIndex('vastu', VASTU_CHAPTERS),
  ...buildChapterIndex('swara-vigyan', SWARA_CHAPTERS),
  ...buildChapterIndex('yantra-vidya', YANTRA_CHAPTERS),
  ...buildChapterIndex('tantra', TANTRA_CHAPTERS),
];

export function searchSyllabusTopics(
  query: string,
  schoolSlug?: SearchableSchoolSlug,
): GlobalSearchResult[] {
  const q = foldSearchText(query).trim();
  if (!q) return [];

  const queryWords = q.split(/\s+/).filter((w) => w.length >= 2);
  const pool = schoolSlug
    ? SYLLABUS_SEARCH_INDEX.filter((entry) => entry.schoolSlug === schoolSlug)
    : SYLLABUS_SEARCH_INDEX;

  const scored = pool
    .map((entry) => {
      const rank = scoreSearchMatch(
        {
          labelEn: entry.titleEn,
          labelHi: entry.titleHi,
          groupEn: entry.groupLabelEn,
          groupHi: entry.groupLabelHi,
          terms: entry.terms,
        },
        q,
        queryWords,
      );

      if (rank <= 0) return null;

      const labelEnF = foldSearchText(entry.titleEn);
      const labelHiF = foldSearchText(entry.titleHi);
      let matchSnippet = '';
      if (!labelEnF.includes(q) && !labelHiF.includes(q)) {
        matchSnippet = findMatchSnippet(entry.terms, queryWords);
      }

      return {
        result: {
          schoolSlug: entry.schoolSlug,
          topicId: entry.topicId,
          moduleId: entry.moduleId,
          titleEn: entry.titleEn,
          titleHi: entry.titleHi,
          groupLabelEn: entry.groupLabelEn,
          groupLabelHi: entry.groupLabelHi,
          matchSnippet,
          icon: entry.icon,
          rank,
        } satisfies GlobalSearchResult,
        order: entry.order,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  scored.sort((a, b) => {
    if (b.result.rank !== a.result.rank) return b.result.rank - a.result.rank;
    return a.order - b.order;
  });

  return scored.map((item) => item.result);
}
